import { createContext, type ReactNode, useEffect, useMemo, useState } from "react";
import {
  AuthProvider as OidcAuthProvider,
} from "react-oidc-context";
import { AuthStartupOverlay } from "./AuthStartupOverlay";
import { AuthProviderInner } from "./AuthProviderInner";
import { createOidcConfig } from "./config";
import {
  getAuthAvailabilityController,
  subscribeToAuthAvailability,
} from "./availability";
import type { AuthConfig, AuthAvailabilityState, AuthContextValue } from "./types";

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  config: AuthConfig;
  children: ReactNode;
}

/**
 * Authentication provider that wraps the OIDC context and provides
 * a simplified API for the application. Delays OIDC initialization until
 * the discovery endpoint is available.
 */
export function AuthProvider({ config, children }: AuthProviderProps) {
  const oidcConfig = useMemo(() => createOidcConfig(config), [config]);
  const [availabilityState, setAvailabilityState] = useState<AuthAvailabilityState>(
    () => getAuthAvailabilityController(config.authority).state,
  );

  // Callback to clean up URL after signin redirect
  const onSigninCallback = () => {
    // Remove the code/state params from URL
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  useEffect(() => {
    return subscribeToAuthAvailability(config.authority, setAvailabilityState);
  }, [config.authority]);

  if (availabilityState.status !== "ready") {
    return <AuthStartupOverlay state={availabilityState} />;
  }

  return (
    <OidcAuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </OidcAuthProvider>
  );
}
